import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import * as yup from "yup";

import { BiztripLogo } from "~/components/BiztripLogo";
import { Box } from "~/components/Box";
import { Button } from "~/components/Button";
import { Divider } from "~/components/Divider";
import { Flex } from "~/components/Flex";
import { FormControl } from "~/components/FormControl";
import { SvgArrowBack, SvgMail } from "~/components/Icon/icons";
import { TextInput } from "~/components/Input";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import { authService } from "~/application/usecases";
import { snackbarService } from "~/components/SnackbarStack";
import { SNACKBAR_MESSAGES } from "~/presentation/core/containers/AppContainer/components/ProfileMenu";
import { logError } from "~/presentation/shared/utils/errors";
import { styled } from "~/application/theme";
import { Topography } from "~/components/Topography";

const LOG_TAG = "Auth/pages/RedefinePasswordPage/RedefinePasswordContainer";

const PageContainer = styled("div", {
  width: "100vw",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff",
  position: "relative",
  overflow: "hidden",
  padding: "$4",
});

const BackgroundWrapper = styled("div", {
  position: "absolute",
  inset: 0,
  zIndex: 0,
});

const CardContainer = styled("div", {
  backgroundColor: "$neutrals-white",
  borderRadius: "$mdlg",
  boxShadow: "0 4px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
  width: "100%",
  maxWidth: "480px",
  padding: "$12 $12",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "$6",
  position: "relative",
  zIndex: 1,
  "@mxsm": {
    padding: "$8 $6",
    maxWidth: "100%",
  },
});

export type RedefinePasswordFormData = {
  email: string;
};

const defaultData: RedefinePasswordFormData = {
  email: "",
};

const redefinePasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email("Informe um e-mail válido")
    .required("O campo e-mail é obrigatório"),
});

export function RedefinePasswordContainer() {
  const [isEmailSent, setIsEmailSent] = useState(false);

  const navigate = useNavigate();

  const { formState: { errors, isSubmitting }, control, handleSubmit } = useForm<RedefinePasswordFormData>({
    defaultValues: defaultData,
    resolver: yupResolver(redefinePasswordSchema)
  });

  const { mutate: mutateRequestRedefinePassword, isLoading } = useMutation(
    authService.forgotPassword,
    {
      onSuccess: () => {
        snackbarService.showSnackbar(
          SNACKBAR_MESSAGES.REQUEST_PASSWORD_CHANGE_SUCCESS_MESSAGE,
          "success"
        );

        setIsEmailSent(true);
      },
      onError: (error) => {
        logError({
          error,
          logTag: LOG_TAG,
          defaultErrorMessage: SNACKBAR_MESSAGES.REQUEST_PASSWORD_CHANGE_ERROR_MESSAGE,
        });
      },
    }
  );

  function redefinePassword(data: RedefinePasswordFormData) {
    mutateRequestRedefinePassword(data.email);
  }

  return (
    <PageContainer>
      <BackgroundWrapper>
        <Topography lowColor="#3B82F6" midColor="#3B82F6" highColor="#ffffff" scale={2} />
      </BackgroundWrapper>

      <CardContainer>
        <Flex justify="center" direction="column" gap="3" css={{ position: "relative", width: "100%" }}>
          <BiztripLogo css={{ margin: "0 auto" }} size={{ "@initial": "md", "@mxmd": "lg" }} />

          <Button
            variant="none"
            onClick={() => navigate(-1)}
            css={{
              width: "fit-content",
              height: "auto",
              position: "absolute",
              top: 0,
              left: "-16px",
              "&:hover": { cursor: "pointer" }
            }}
          >
            <SvgArrowBack />
          </Button>

          {isEmailSent && (
            <>
              <Text
                size="4"
                as="h1"
                css={{
                  fw: "700",
                  textAlign: "center",
                }}
              >
                E-mail enviado!
              </Text>

              <Text size="4" css={{ textAlign: "center", lineHeight: "$5" }}>
                Caso o e-mail esteja cadastrado no sistema, você receberá uma mensagem com as instruções para definir uma nova senha
              </Text>

              <Divider />

              <Text size="3" css={{ textAlign: "center", lineHeight: "$5", color: "$neutrals-base" }}>
                Caso não tenha recebida a mensagem, verifique se digitou o endereço de e-mail corretamente ou se caiu na caixa de spam
              </Text>

              <Button
                variant="tertiary"
                css={{ width: "100%" }}
                onClick={() => setIsEmailSent(false)}
              >Reenviar e-mail</Button>
            </>
          )}

          {!isEmailSent && (
            <>
              <Text
                size="6"
                as="h1"
                css={{
                  fw: "700",
                  textAlign: "center",
                  color: "$neutrals-darkest",
                }}
              >
                Redefinição de senha
              </Text>

              <Text size="3" css={{ textAlign: "center", lineHeight: "$5", color: "$neutral-600", mb: "$2" }}>
                Informe seu e-mail cadastrado para enviarmos as instruções de redefinição de senha
              </Text>

              <form onSubmit={handleSubmit(redefinePassword)} style={{ width: "100%" }}>
                <Flex direction="column" gap="4" css={{ width: "100%" }}>
                  <Box>
                    <FormControl name="email" control={control}>
                      <TextInput placeholder="E-mail" leftIcon={SvgMail} />
                      {errors.email && (
                        <Text variant="error-base" size="3" css={{ mt: "$2" }}>{errors.email.message}</Text>
                      )}
                    </FormControl>
                  </Box>

                  <Button
                    type="submit"
                    disabled={isSubmitting || isLoading}
                    variant="primary"
                    size="mdl"
                    rounded
                    fullWidth
                    css={{ fontSize: "$md", fontWeight: 600, mt: "$2" }}
                  >
                    {isSubmitting && <Spinner css={{ borderLeftColor: "$neutrals-white", width: "$4", height: "$4", borderWidth: "2px", mr: "$2" }} />}
                    Continuar
                  </Button>
                </Flex>
              </form>
            </>
          )}
        </Flex>
      </CardContainer>
    </PageContainer>
  );
}